import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    ContactView,
    OpportunityContactBuildingContactRoleView,
    OpportunityContactView,
    SearchAvailableOpportunityContactsParameters
} from '@api-proxies';
import { OpportunityContactService } from '@http/clients/opportunity-contact.service';
import { OpportunityContact } from '@models/OpportunityContact';
import { ContactPositions } from '@models/enums/ContactPositions';
import { TabNames } from '@models/enums/TabNames';
import {
    EditorComponentBase,
    GenericTypeAheadMultiselectComponent,
    HighlightTextPipe,
    ITypeAheadDropdownAction,
    ModalService,
    ObjectExtensionsService
} from 'ngx-lib';
import { ModalAddContactComponent } from '../../modals/modal-add-contact/modal-add-contact.component';

type RoutableOpportunityContact = OpportunityContact & { route: string };
@Component({
    selector: 'app-opportunity-contact-editor',
    templateUrl: './opportunity-contact-editor.component.html',
    styleUrls: ['./opportunity-contact-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericTypeAheadMultiselectComponent, HighlightTextPipe]
})
export class OpportunityContactEditorComponent extends EditorComponentBase<OpportunityContactView[]> implements OnInit {
    contactItems: OpportunityContact[];
    selectedContactItems?: RoutableOpportunityContact[];
    searchText: string;
    contactPosition?: ContactPositions;
    addContactAction!: ITypeAheadDropdownAction;

    constructor(
        private readonly opportunityContactService: OpportunityContactService,
        private readonly objectExtensionService: ObjectExtensionsService,
        private readonly modalService: ModalService
    ) {
        super();
        this.isReadOnly = !!this.bindContext;
        this.searchText = '';
        this.contactItems = [];
        this.contactPosition = undefined;
    }

    ngOnInit(): void {
        this.contactPosition = this.additionalInfo as ContactPositions;

        this.addContactAction = {
            includeAction: true,
            name: '+ Add new contact',
            action: () => this.openModal()
        };

        if (this.bindingProperty) this.filterContactsByRole();
    }

    async onContactTextChange(event: string) {
        const results = await this.opportunityContactService.searchAvailable({
            filterText: event
        } as SearchAvailableOpportunityContactsParameters);
        this.contactItems = results.map((x) => OpportunityContact.fromContactView(x));
    }

    onSelectedContactChange(event: OpportunityContact[]) {
        this.selectedContactItems = event.map(this.addRoute);
        const clonedBindingProperty = this.objectExtensionService.clone(
            this.bindingProperty
        ) as OpportunityContactView[];

        event.forEach((currentContact) => {
            const existingContact = clonedBindingProperty.find(
                (contact) => contact.contactId === currentContact.contactId
            );
            if (existingContact) {
                if (
                    !existingContact.opportunityContactBuildingContactRoles?.some(
                        (x) => x.buildingContactRoleId === this.contactPosition
                    )
                ) {
                    existingContact.opportunityContactBuildingContactRoles?.push({
                        buildingContactRoleId: this.contactPosition
                    } as OpportunityContactBuildingContactRoleView);
                }
            } else {
                clonedBindingProperty?.push(this.mapToOpportunityContact(currentContact));
            }
            this.bindingProperty = clonedBindingProperty;
            this.valueChange.emit();
        });
    }

    onRemoveContactChange(event: OpportunityContact) {
        if (this.bindingProperty) {
            const clonedBindingProperty = this.objectExtensionService.clone(
                this.bindingProperty
            ) as OpportunityContactView[];

            const contactIndex = clonedBindingProperty?.findIndex((contact) => contact.contactId === event.contactId);
            if (contactIndex !== undefined && contactIndex !== -1) {
                const contact = clonedBindingProperty[contactIndex];

                const positionIndex = contact.opportunityContactBuildingContactRoles?.findIndex((contactRoles) => {
                    return contactRoles.buildingContactRoleId === this.contactPosition;
                });

                if (positionIndex !== undefined && positionIndex !== -1) {
                    clonedBindingProperty[contactIndex].opportunityContactBuildingContactRoles?.splice(
                        positionIndex,
                        1
                    );

                    if (contact.opportunityContactBuildingContactRoles?.length === 0) {
                        clonedBindingProperty.splice(contactIndex, 1);
                    }
                    this.bindingProperty = clonedBindingProperty;
                }
                this.valueChange.emit();
            }
        }
    }

    async openModal(): Promise<void> {
        const contact = await this.modalService.open(ModalAddContactComponent, {
            setCompany: true
        });
        if (contact) {
            const clonedBindingProperty = this.objectExtensionService.clone(
                this.bindingProperty
            ) as OpportunityContactView[];

            clonedBindingProperty?.push(this.mapToOpportunityContact(contact));

            this.bindingProperty = clonedBindingProperty;

            this.filterContactsByRole();

            this.valueChange.emit();
        }
    }

    filterContactsByRole() {
        const mappedResults = this.bindingProperty?.map((x) => OpportunityContact.fromInterface(x));

        this.selectedContactItems = mappedResults
            ?.filter((contact) =>
                contact.opportunityContactBuildingContactRoles?.some(
                    (x) => x.buildingContactRoleId === this.contactPosition
                )
            )
            ?.map(this.addRoute);
    }

    mapToOpportunityContact(contact: OpportunityContact | ContactView): OpportunityContact {
        return OpportunityContact.fromInterface({
            contactId: contact.id,
            companyId: contact.companyId,
            firstName: contact.firstName,
            lastName: contact.lastName,
            fullName: contact.fullName,
            phone: contact.phone,
            email: contact.email,
            opportunityContactBuildingContactRoles: [{ buildingContactRoleId: this.contactPosition }],
            isActive: contact.isActive
        } as OpportunityContactView);
    }

    private addRoute = (contact: OpportunityContact): RoutableOpportunityContact => {
        const routableContact = OpportunityContact.fromInterface(contact);
        (routableContact as RoutableOpportunityContact).route =
            `contacts-single/${contact.contactId}/${TabNames[TabNames.Overview]}`;
        return routableContact as RoutableOpportunityContact;
    };
}
